<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    revealAt?: number
    holdAfter?: number
    fullscreen?: boolean
    alt?: string
  }>(),
  {
    revealAt: 2.05,
    holdAfter: 300,
    fullscreen: true,
    alt: 'WLearn Academy',
  },
)

const emit = defineEmits<{ done: [] }>()
const reveal = ref<HTMLElement>()
let holdTimer: ReturnType<typeof setTimeout>
let fallbackTimer: ReturnType<typeof setTimeout>
let finished = false

const finish = () => {
  if (finished) return
  finished = true
  clearTimeout(fallbackTimer)
  clearTimeout(holdTimer)
  holdTimer = setTimeout(() => emit('done'), props.holdAfter)
}

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finish()
    return
  }

  reveal.value?.addEventListener('animationend', finish, { once: true })
  fallbackTimer = setTimeout(finish, (props.revealAt + 1.2) * 1000)
})

onBeforeUnmount(() => {
  clearTimeout(holdTimer)
  clearTimeout(fallbackTimer)
})
</script>

<template>
  <div
    class="splash"
    :class="{ 'splash--fixed': fullscreen }"
    :style="{ '--reveal-at': `${revealAt}s` }"
  >
    <div class="layer layer--blue">
      <div class="sparkles" aria-hidden="true">
        <i
          v-for="index in 6"
          :key="index"
          :class="`sparkle sparkle--${index}`"
        />
      </div>
      <img class="logo" src="/img/mascot/logo-white.webp" :alt="alt" >
    </div>

    <div ref="reveal" class="layer layer--white">
      <img
        class="logo"
        src="/img/mascot/logo-blue.webp"
        alt=""
        aria-hidden="true"
      >
    </div>

    <div class="ring" aria-hidden="true" />
    <div class="burst" aria-hidden="true">
      <i v-for="index in 8" :key="index" :class="`dot dot--${index}`" />
    </div>

    <div class="bird" aria-hidden="true">
      <div class="bird__enter">
        <div class="bird__pop">
          <div class="bird__bob">
            <img src="/img/mascot/mascot-splash.webp" alt="" >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.splash,
.splash * {
  box-sizing: border-box;
}
.splash {
  --size: var(--wl-splash-unit, min(100vw, 100vh));
  --blue: var(--wl-splash-blue, #3577df);
  --white: var(--wl-splash-white, #fff);
  --bird-y: 40%;
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: var(--blue);
}
.splash--fixed {
  position: fixed;
  z-index: 100;
  animation: splash-failsafe 0s linear 4s forwards;
}
.layer {
  position: absolute;
  inset: 0;
}
.layer--blue {
  background: var(--blue);
}
.layer--white {
  background: var(--white);
  clip-path: circle(0 at 50% 40%);
  animation: reveal 0.8s cubic-bezier(0.5, 0.15, 0.25, 1) var(--reveal-at) both;
}

@keyframes splash-failsafe {
  to {
    visibility: hidden;
    pointer-events: none;
  }
}
.logo {
  position: absolute;
  top: 66%;
  left: 50%;
  width: min(60vw, calc(var(--size) * 0.6));
  transform: translate(-50%, -50%);
}
.layer--blue .logo {
  animation: logo-in 0.62s cubic-bezier(0.2, 0.8, 0.3, 1) 0.2s both;
}
.layer--white .logo {
  top: 69%;
  width: min(48vw, calc(var(--size) * 0.48));
}
.sparkles,
.burst {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.sparkle,
.dot {
  position: absolute;
  display: block;
  border-radius: 50%;
}
.sparkle {
  width: calc(var(--size) * 0.03);
  aspect-ratio: 1;
  opacity: 0.2;
  background: linear-gradient(
    135deg,
    var(--wl-sparkle-a, #ffe4a3),
    var(--wl-sparkle-b, #ffb020)
  );
  clip-path: polygon(
    50% 0,
    61% 39%,
    100% 50%,
    61% 61%,
    50% 100%,
    39% 61%,
    0 50%,
    39% 39%
  );
  animation: twinkle var(--duration) ease-in-out infinite var(--delay);
}
.sparkle--1 {
  left: 16%;
  top: 26%;
  --duration: 2.4s;
  --delay: 0.55s;
}
.sparkle--2 {
  right: 19%;
  top: 21%;
  --duration: 1.8s;
  --delay: 0.85s;
}
.sparkle--3 {
  left: 24%;
  top: 52%;
  --duration: 2.1s;
  --delay: 1.15s;
}
.sparkle--4 {
  right: 15%;
  top: 48%;
  --duration: 2.7s;
  --delay: 0.7s;
}
.sparkle--5 {
  left: 32%;
  top: 15%;
  --duration: 1.6s;
  --delay: 1.3s;
}
.sparkle--6 {
  right: 28%;
  top: 58%;
  --duration: 2.3s;
  --delay: 0.95s;
}
.ring {
  position: absolute;
  left: 50%;
  top: var(--bird-y);
  width: var(--size);
  aspect-ratio: 1;
  border: calc(var(--size) * 0.011) solid var(--white);
  border-radius: 50%;
  pointer-events: none;
  transform: translate(-50%, -50%) scale(0.02);
  opacity: 0;
  animation: ring 0.8s cubic-bezier(0.5, 0.15, 0.25, 1)
    calc(var(--reveal-at) - 0.06s) both;
}
.dot {
  left: 50%;
  top: var(--bird-y);
  width: calc(var(--size) * 0.026);
  aspect-ratio: 1;
  background: var(--dot-color);
  animation: dot 0.62s cubic-bezier(0.2, 0.7, 0.3, 1)
    calc(var(--reveal-at) - 0.02s) both;
}
.dot--1 {
  --x: -140%;
  --y: -320%;
  --dot-color: #ffd37a;
}
.dot--2 {
  --x: 180%;
  --y: -300%;
  --dot-color: #fff;
}
.dot--3 {
  --x: -340%;
  --y: -120%;
  --dot-color: #fff;
}
.dot--4 {
  --x: 360%;
  --y: -90%;
  --dot-color: #ffd37a;
}
.dot--5 {
  --x: -300%;
  --y: 200%;
  --dot-color: #fff;
}
.dot--6 {
  --x: 280%;
  --y: 240%;
  --dot-color: #ffd37a;
}
.dot--7 {
  --x: -90%;
  --y: 340%;
  --dot-color: #ffd37a;
}
.dot--8 {
  --x: 120%;
  --y: 360%;
  --dot-color: #fff;
}
.bird {
  position: absolute;
  left: 50%;
  top: 38%;
  width: min(38vw, calc(var(--size) * 0.38));
  pointer-events: none;
  transform: translate(-50%, -50%);
}
.bird__enter {
  animation: enter 0.66s cubic-bezier(0.22, 1.4, 0.5, 1) both;
}
.bird__pop {
  animation: pop 0.8s cubic-bezier(0.4, 0, 0.3, 1) calc(var(--reveal-at) - 0.3s)
    both;
}
/* .bird__bob {
  animation: bob 0.56s ease-in-out infinite;
} */
.bird img {
  display: block;
  width: 100%;
}
@keyframes reveal {
  to {
    clip-path: circle(110% at 50% 40%);
  }
}
@keyframes logo-in {
  from {
    opacity: 0;
    transform: translate(-50%, -35%) scale(0.94);
  }
}
@keyframes twinkle {
  50% {
    opacity: 0.95;
    transform: scale(1.25) rotate(10deg);
  }
}
@keyframes ring {
  10% {
    opacity: 0.95;
  }
  to {
    transform: translate(-50%, -50%) scale(3.2);
    opacity: 0;
  }
}
@keyframes dot {
  18% {
    opacity: 1;
  }
  to {
    transform: translate(var(--x), var(--y)) scale(0.5);
    opacity: 0;
  }
}
@keyframes enter {
  from {
    opacity: 0;
    transform: translateY(-16%) scale(0.7);
  }
  60% {
    transform: translateY(1%) scale(1.05);
  }
}
@keyframes pop {
  26% {
    transform: translateY(2.5%) scale(1.07, 0.9);
  }
  46% {
    transform: translateY(-7%) scale(0.95, 1.08);
  }
  62% {
    transform: translateY(-2%) scale(1.02, 0.99);
  }
}
@keyframes bob {
  50% {
    transform: translateY(-2.8%) rotate(-1deg);
  }
}
@media (max-width: 640px) {
  .bird {
    top: 35%;
    width: min(76vw, calc(var(--size) * 0.48));
  }
  .layer--white .logo {
    top: 56%;
    width: min(68vw, calc(var(--size) * 0.5));
  }
}
@media (prefers-reduced-motion: reduce) {
  .layer--white {
    animation: none;
    clip-path: none;
  }
  .ring,
  .dot,
  .sparkle,
  .bird__enter,
  .bird__pop,
  .bird__bob {
    animation: none;
  }
  .ring,
  .dot,
  .sparkle,
  .layer--blue .logo {
    display: none;
  }
}
</style>
